import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMemberStats } from 'src/shared/services/memberService';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { LargeCounter } from 'src/modules/dashboard/components/atoms/largeCounter';
import { MemberStats } from 'src/modules/dashboard/models/memberStats';
import { useDashboardFilter } from '../dashboardFilterContext';

const useMemberStats = () => {
  const isMounted = useIsMounted();
  const { associationId, districtId } = useDashboardFilter();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<MemberStats>();

  useEffect(() => {
    setIsLoading(true);
    getMemberStats(associationId, districtId)
      .then((res) => {
        if (isMounted()) setStats(res.data);
      })
      .catch((e) => ConsoleHelper.error(e))
      .finally(() => {
        if (isMounted()) setIsLoading(false);
      });
  }, [isMounted, associationId, districtId]);

  return { stats, isLoading };
};

const StatCard = ({ label, subtitle, count, isLoading }: { label: string; subtitle: string; count: number; isLoading: boolean }) => (
  <Stack sx={{ p: 2, height: '100%' }} spacing={0.5}>
    <Box>
      <HeaderSubTitle label={label} />
      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{subtitle}</Typography>
    </Box>
    {isLoading ? <Skeleton variant='text' sx={{ fontSize: (theme) => theme.typography.h3 }} /> : <LargeCounter count={count} displaySuffix />}
  </Stack>
);

export const MainMemberWidget = () => {
  const { t } = useTranslation(['dashboard']);
  const { stats, isLoading } = useMemberStats();
  return (
    <StatCard
      label={t('dashboard:members.memberType.ordinary')}
      subtitle='Antal, betalda + obetalda'
      count={stats?.nrOfOrdinary ?? 0}
      isLoading={isLoading}
    />
  );
};

export const HouseholdMemberWidget = () => {
  const { t } = useTranslation(['dashboard']);
  const { stats, isLoading } = useMemberStats();
  return (
    <StatCard
      label={t('dashboard:members.memberType.family')}
      subtitle='Antal, betalda + obetalda'
      count={stats?.nrOfFamily ?? 0}
      isLoading={isLoading}
    />
  );
};
